/**
 * Intervention Dashboard Component
 *
 * Manages student interventions with real backend data: listing and filtering,
 * manual creation, progress notes, completion, and aggregate analytics.
 *
 * AI-generated recommendations are shown as "not available yet" because the
 * backend's predictive risk model has not been deployed.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';

import {
  Intervention,
  InterventionAnalytics,
  InterventionCreateInput,
  StudentOption,
  EffectivenessRating,
  listInterventions,
  getInterventionAnalytics,
  listStudentOptions,
  createIntervention,
  updateInterventionProgress,
} from '../../services/interventionService';
import { Button } from '../common/Button';
import { Card } from '../common/Card';

interface InterventionDashboardProps {
  studentId?: string;
  viewMode?: 'dashboard' | 'student' | 'recommendations';
  onInterventionUpdate?: (intervention: Intervention) => void;
}

const INTERVENTION_TYPES = [
  { value: 'academic', label: 'Academic' },
  { value: 'behavioral', label: 'Behavioral' },
  { value: 'social-emotional', label: 'Social-Emotional' },
  { value: 'attendance', label: 'Attendance' },
];

const STATUS_STYLES: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  completed: 'bg-blue-100 text-blue-800',
  discontinued: 'bg-gray-100 text-gray-700',
};

const TIER_STYLES: Record<number, string> = {
  1: 'bg-emerald-100 text-emerald-800',
  2: 'bg-amber-100 text-amber-800',
  3: 'bg-red-100 text-red-800',
};

const EMPTY_CREATE_FORM = {
  student_id: '',
  intervention_name: '',
  intervention_type: 'academic',
  tier_level: '1',
  start_date: new Date().toISOString().slice(0, 10),
  frequency: '',
  provider: '',
  target_goals: '',
  success_criteria: '',
  notes: '',
};

export const InterventionDashboard: React.FC<InterventionDashboardProps> = ({
  studentId,
  onInterventionUpdate,
}) => {
  const [activeTab, setActiveTab] = useState<
    'interventions' | 'analytics' | 'recommendations'
  >('interventions');
  const [interventions, setInterventions] = useState<Intervention[]>([]);
  const [analytics, setAnalytics] = useState<InterventionAnalytics | null>(
    null
  );
  const [students, setStudents] = useState<StudentOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    student: studentId || '',
    status: '',
    type: '',
    tier: '',
  });
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [noteDrafts, setNoteDrafts] = useState<Record<number, string>>({});

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [interventionData, analyticsData, studentData] = await Promise.all(
        [listInterventions(), getInterventionAnalytics(), listStudentOptions()]
      );
      setInterventions(interventionData);
      setAnalytics(analyticsData);
      setStudents(studentData);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load intervention data'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredInterventions = useMemo(() => {
    return interventions.filter(intervention => {
      if (
        filters.student &&
        String(intervention.student_id) !== filters.student
      )
        return false;
      if (filters.status && intervention.status !== filters.status)
        return false;
      if (filters.type && intervention.intervention_type !== filters.type)
        return false;
      if (filters.tier && String(intervention.tier_level) !== filters.tier)
        return false;
      return true;
    });
  }, [interventions, filters]);

  const replaceIntervention = useCallback(
    (updated: Intervention) => {
      setInterventions(prev =>
        prev.map(item => (item.id === updated.id ? updated : item))
      );
      onInterventionUpdate?.(updated);
    },
    [onInterventionUpdate]
  );

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    const goals = createForm.target_goals
      .split('\n')
      .map(s => s.trim())
      .filter(Boolean);
    const criteria = createForm.success_criteria
      .split('\n')
      .map(s => s.trim())
      .filter(Boolean);

    if (!createForm.student_id) {
      setFormError('Select a student.');
      return;
    }
    if (goals.length === 0 || criteria.length === 0) {
      setFormError('Enter at least one goal and one success criterion.');
      return;
    }

    const input: InterventionCreateInput = {
      student_id: Number(createForm.student_id),
      intervention_name: createForm.intervention_name,
      intervention_type: createForm.intervention_type,
      tier_level: Number(createForm.tier_level),
      start_date: createForm.start_date,
      provider: createForm.provider,
      target_goals: goals,
      success_criteria: criteria,
    };
    if (createForm.frequency) input.frequency = createForm.frequency;
    if (createForm.notes) input.notes = createForm.notes;

    setSaving(true);
    try {
      const created = await createIntervention(input);
      setInterventions(prev => [created, ...prev]);
      setShowCreateForm(false);
      setCreateForm(EMPTY_CREATE_FORM);
      getInterventionAnalytics().then(setAnalytics).catch(() => undefined);
    } catch (err) {
      setFormError(
        err instanceof Error ? err.message : 'Failed to create intervention'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleAddNote = async (interventionId: number) => {
    const note = (noteDrafts[interventionId] || '').trim();
    if (!note) return;
    try {
      const updated = await updateInterventionProgress(interventionId, {
        progress_note: note,
      });
      replaceIntervention(updated);
      setNoteDrafts(prev => ({ ...prev, [interventionId]: '' }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add note');
    }
  };

  const handleComplete = async (
    interventionId: number,
    effectiveness: EffectivenessRating
  ) => {
    try {
      const updated = await updateInterventionProgress(interventionId, {
        status: 'completed',
        effectiveness_rating: effectiveness,
        end_date: new Date().toISOString().slice(0, 10),
      });
      replaceIntervention(updated);
      getInterventionAnalytics().then(setAnalytics).catch(() => undefined);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to complete intervention'
      );
    }
  };

  const handleDiscontinue = async (interventionId: number) => {
    try {
      const updated = await updateInterventionProgress(interventionId, {
        status: 'discontinued',
        end_date: new Date().toISOString().slice(0, 10),
      });
      replaceIntervention(updated);
      getInterventionAnalytics().then(setAnalytics).catch(() => undefined);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to discontinue intervention'
      );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
        <span className="ml-3 text-gray-600">Loading interventions...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <Card className="p-4 bg-red-50 border-red-200">
          <div className="flex items-center justify-between">
            <p className="text-red-700 text-sm">{error}</p>
            <Button onClick={loadData} variant="outline" size="sm">
              Retry
            </Button>
          </div>
        </Card>
      )}

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {(
            [
              ['interventions', 'Interventions'],
              ['analytics', 'Analytics'],
              ['recommendations', 'AI Recommendations'],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === key
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'interventions' && (
        <div className="space-y-6">
          {/* Filters + create */}
          <Card className="p-4">
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <label
                  htmlFor="student-filter"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Student
                </label>
                <select
                  id="student-filter"
                  value={filters.student}
                  onChange={e =>
                    setFilters(prev => ({ ...prev, student: e.target.value }))
                  }
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Students</option>
                  {students.map(s => (
                    <option key={s.id} value={String(s.id)}>
                      {s.name} (Grade {s.grade_level})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  htmlFor="status-filter"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Status
                </label>
                <select
                  id="status-filter"
                  value={filters.status}
                  onChange={e =>
                    setFilters(prev => ({ ...prev, status: e.target.value }))
                  }
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                  <option value="discontinued">Discontinued</option>
                </select>
              </div>
              <div>
                <label
                  htmlFor="type-filter"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Type
                </label>
                <select
                  id="type-filter"
                  value={filters.type}
                  onChange={e =>
                    setFilters(prev => ({ ...prev, type: e.target.value }))
                  }
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Types</option>
                  {INTERVENTION_TYPES.map(t => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  htmlFor="tier-filter"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Tier
                </label>
                <select
                  id="tier-filter"
                  value={filters.tier}
                  onChange={e =>
                    setFilters(prev => ({ ...prev, tier: e.target.value }))
                  }
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Tiers</option>
                  <option value="1">Tier 1</option>
                  <option value="2">Tier 2</option>
                  <option value="3">Tier 3</option>
                </select>
              </div>
              <div className="ml-auto">
                <Button onClick={() => setShowCreateForm(v => !v)}>
                  {showCreateForm ? 'Cancel' : 'New Intervention'}
                </Button>
              </div>
            </div>
          </Card>

          {/* Create form */}
          {showCreateForm && (
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                New Intervention
              </h3>
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="create-student"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Student *
                    </label>
                    <select
                      id="create-student"
                      required
                      value={createForm.student_id}
                      onChange={e =>
                        setCreateForm(prev => ({
                          ...prev,
                          student_id: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select a student</option>
                      {students.map(s => (
                        <option key={s.id} value={String(s.id)}>
                          {s.name} (Grade {s.grade_level})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label
                      htmlFor="create-name"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Intervention Name *
                    </label>
                    <input
                      id="create-name"
                      type="text"
                      required
                      maxLength={200}
                      value={createForm.intervention_name}
                      onChange={e =>
                        setCreateForm(prev => ({
                          ...prev,
                          intervention_name: e.target.value,
                        }))
                      }
                      placeholder="e.g. Daily reading fluency practice"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="create-type"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Type *
                    </label>
                    <select
                      id="create-type"
                      value={createForm.intervention_type}
                      onChange={e =>
                        setCreateForm(prev => ({
                          ...prev,
                          intervention_type: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {INTERVENTION_TYPES.map(t => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label
                      htmlFor="create-tier"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      RTI Tier *
                    </label>
                    <select
                      id="create-tier"
                      value={createForm.tier_level}
                      onChange={e =>
                        setCreateForm(prev => ({
                          ...prev,
                          tier_level: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="1">Tier 1 (universal)</option>
                      <option value="2">Tier 2 (targeted)</option>
                      <option value="3">Tier 3 (intensive)</option>
                    </select>
                  </div>
                  <div>
                    <label
                      htmlFor="create-start"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Start Date *
                    </label>
                    <input
                      id="create-start"
                      type="date"
                      required
                      value={createForm.start_date}
                      onChange={e =>
                        setCreateForm(prev => ({
                          ...prev,
                          start_date: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="create-provider"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Provider *
                    </label>
                    <input
                      id="create-provider"
                      type="text"
                      required
                      maxLength={200}
                      value={createForm.provider}
                      onChange={e =>
                        setCreateForm(prev => ({
                          ...prev,
                          provider: e.target.value,
                        }))
                      }
                      placeholder="e.g. Reading specialist"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="create-frequency"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Frequency
                    </label>
                    <input
                      id="create-frequency"
                      type="text"
                      maxLength={100}
                      value={createForm.frequency}
                      onChange={e =>
                        setCreateForm(prev => ({
                          ...prev,
                          frequency: e.target.value,
                        }))
                      }
                      placeholder='e.g. "3x per week"'
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="create-goals"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Target Goals * (one per line)
                    </label>
                    <textarea
                      id="create-goals"
                      required
                      rows={3}
                      value={createForm.target_goals}
                      onChange={e =>
                        setCreateForm(prev => ({
                          ...prev,
                          target_goals: e.target.value,
                        }))
                      }
                      placeholder="Increase oral reading fluency to 90 wcpm"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="create-criteria"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Success Criteria * (one per line)
                    </label>
                    <textarea
                      id="create-criteria"
                      required
                      rows={3}
                      value={createForm.success_criteria}
                      onChange={e =>
                        setCreateForm(prev => ({
                          ...prev,
                          success_criteria: e.target.value,
                        }))
                      }
                      placeholder="Weekly probe shows +2 wcpm growth"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="create-notes"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Notes
                  </label>
                  <textarea
                    id="create-notes"
                    rows={2}
                    value={createForm.notes}
                    onChange={e =>
                      setCreateForm(prev => ({
                        ...prev,
                        notes: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                {formError && (
                  <p className="text-sm text-red-600">{formError}</p>
                )}
                <div className="flex justify-end gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowCreateForm(false);
                      setFormError(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={saving}>
                    {saving ? 'Creating...' : 'Create Intervention'}
                  </Button>
                </div>
              </form>
            </Card>
          )}

          {/* Intervention list */}
          {filteredInterventions.length === 0 ? (
            <Card className="p-10 text-center">
              <p className="text-gray-600 font-medium">
                No interventions found
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {interventions.length === 0
                  ? 'No interventions have been created yet. Use "New Intervention" to assign the first one.'
                  : 'No interventions match the current filters.'}
              </p>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredInterventions.map(intervention => (
                <Card key={intervention.id} className="p-6">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {intervention.intervention_name}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {intervention.student_name} &middot;{' '}
                        {intervention.provider}
                        {intervention.frequency
                          ? ` · ${intervention.frequency}`
                          : ''}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          TIER_STYLES[intervention.tier_level] ||
                          'bg-gray-100 text-gray-700'
                        }`}
                      >
                        Tier {intervention.tier_level}
                      </span>
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                        {intervention.intervention_type}
                      </span>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          STATUS_STYLES[intervention.status] ||
                          'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {intervention.status}
                      </span>
                      {intervention.effectiveness_rating && (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-800">
                          {intervention.effectiveness_rating.replace(/_/g, ' ')}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="font-medium text-gray-700 mb-1">Goals</p>
                      <ul className="list-disc list-inside text-gray-600 space-y-0.5">
                        {intervention.target_goals.map((goal, idx) => (
                          <li key={idx}>{goal}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="font-medium text-gray-700 mb-1">
                        Success Criteria
                      </p>
                      <ul className="list-disc list-inside text-gray-600 space-y-0.5">
                        {intervention.success_criteria.map(
                          (criterion, idx) => (
                            <li key={idx}>{criterion}</li>
                          )
                        )}
                      </ul>
                    </div>
                  </div>

                  <p className="mt-3 text-xs text-gray-500">
                    {intervention.start_date
                      ? `Started ${intervention.start_date}`
                      : ''}
                    {intervention.end_date
                      ? ` · Ended ${intervention.end_date}`
                      : ''}
                  </p>

                  {intervention.progress_data.length > 0 && (
                    <div className="mt-4">
                      <p className="font-medium text-gray-700 text-sm mb-1">
                        Progress Notes
                      </p>
                      <ul className="space-y-1">
                        {intervention.progress_data.map((entry, idx) => (
                          <li
                            key={idx}
                            className="text-sm text-gray-600 bg-gray-50 rounded px-3 py-2"
                          >
                            <span className="text-xs text-gray-400 mr-2">
                              {entry.date?.slice(0, 10)} — {entry.recorded_by}
                            </span>
                            {entry.note}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {intervention.status === 'active' && (
                    <div className="mt-4 border-t border-gray-100 pt-4 space-y-3">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={noteDrafts[intervention.id] || ''}
                          onChange={e =>
                            setNoteDrafts(prev => ({
                              ...prev,
                              [intervention.id]: e.target.value,
                            }))
                          }
                          placeholder="Add a progress note..."
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleAddNote(intervention.id)}
                          disabled={!(noteDrafts[intervention.id] || '').trim()}
                        >
                          Add Note
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          onClick={() =>
                            handleComplete(intervention.id, 'effective')
                          }
                        >
                          Complete (Effective)
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            handleComplete(
                              intervention.id,
                              'partially_effective'
                            )
                          }
                        >
                          Complete (Partially Effective)
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            handleComplete(intervention.id, 'not_effective')
                          }
                        >
                          Complete (Not Effective)
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDiscontinue(intervention.id)}
                        >
                          Discontinue
                        </Button>
                      </div>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'analytics' && analytics && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="p-4">
              <p className="text-sm font-medium text-gray-600">
                Total Interventions
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {analytics.summary.total_interventions}
              </p>
            </Card>
            <Card className="p-4">
              <p className="text-sm font-medium text-gray-600">Active</p>
              <p className="text-2xl font-bold text-green-600">
                {analytics.summary.active_interventions}
              </p>
            </Card>
            <Card className="p-4">
              <p className="text-sm font-medium text-gray-600">
                Completion Rate
              </p>
              <p className="text-2xl font-bold text-blue-600">
                {analytics.summary.completion_rate}%
              </p>
            </Card>
            <Card className="p-4">
              <p className="text-sm font-medium text-gray-600">
                Effective Rate
              </p>
              <p className="text-2xl font-bold text-teal-600">
                {analytics.summary.effective_rate !== null
                  ? `${analytics.summary.effective_rate}%`
                  : '—'}
              </p>
              <p className="text-xs text-gray-500">
                of {analytics.summary.rated_interventions} rated
              </p>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {(
              [
                ['By Status', analytics.interventions_by_status],
                ['By Type', analytics.interventions_by_type],
                ['By Tier', analytics.interventions_by_tier],
              ] as const
            ).map(([title, data]) => (
              <Card key={title} className="p-4">
                <h4 className="font-medium text-gray-900 mb-3">{title}</h4>
                {Object.keys(data).length === 0 ? (
                  <p className="text-sm text-gray-500">No data yet</p>
                ) : (
                  <ul className="space-y-2">
                    {Object.entries(data).map(([key, count]) => (
                      <li
                        key={key}
                        className="flex justify-between text-sm text-gray-700"
                      >
                        <span className="capitalize">
                          {key.replace(/_/g, ' ')}
                        </span>
                        <span className="font-semibold">{count}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'recommendations' && (
        <Card className="p-10 text-center">
          <div className="mx-auto w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
            <svg
              className="w-6 h-6 text-purple-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900">
            AI Recommendations Coming Soon
          </h3>
          <p className="text-sm text-gray-600 mt-2 max-w-md mx-auto">
            AI-generated intervention recommendations require a trained
            predictive risk model, which has not been deployed yet. Until
            then, interventions can be created manually from the
            Interventions tab.
          </p>
        </Card>
      )}
    </div>
  );
};

export default InterventionDashboard;
